import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", path: "/" },
  { label: "分类", path: "/category" },
  { label: "专题", path: "/special" },
  { label: "搜索", path: "/search" },
  { label: "关于", path: "/about" },
  { label: "联系", path: "/contact" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    setMobileOpen(false);
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:h-[72px] md:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setMobileOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-semibold tracking-tight text-background-50">
            AV
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[19px] font-semibold tracking-tight text-foreground-950">韩国AV</span>
            <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-500">Korean Film Archive</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-[13px] transition-colors ${
                isActive(link.path)
                  ? "bg-primary-50 font-semibold text-primary-700"
                  : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-[268px] items-center md:flex">
          <div className="relative w-full">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视名称、题材或剧情"
              aria-label="搜索影视作品"
              className="h-9 w-full rounded-md border border-background-300/80 bg-background-50 pl-9 pr-16 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 h-7 -translate-y-1/2 whitespace-nowrap rounded-md bg-primary-500 px-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <Link
            to="/special"
            className="hidden whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-[12px] font-medium text-accent-800 transition-colors hover:bg-accent-100 md:inline-flex"
          >
            本周专题
          </Link>
          <button
            type="button"
            aria-label="打开导航菜单"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/80 text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-3-line text-lg"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视名称"
                aria-label="搜索影视作品"
                className="h-10 w-full rounded-md border border-background-300/80 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-10 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between rounded-md border px-3 py-3 text-sm ${
                  isActive(link.path)
                    ? "border-primary-200 bg-primary-50 text-primary-700"
                    : "border-background-200 bg-background-100 text-foreground-700"
                }`}
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}